import axios from 'axios';
import { mockAdapter } from './mock/adapter';
import { avisarSesionVencida } from '../sesion-events';

/**
 * Modo demo:
 *   VITE_MOCK_MODE=true  → toda request va al adapter in-memory (sin backend)
 *   VITE_MOCK_MODE=false → request real al backend definido por VITE_API_URL
 *
 * Para GH Pages dejamos VITE_MOCK_MODE=true en el workflow de deploy.
 */
const isMockMode = import.meta.env.VITE_MOCK_MODE === 'true';

// Get API URL from env or default
const baseURL = import.meta.env.VITE_API_URL || '/api/v1';

export const apiClient = axios.create({
    baseURL,
    withCredentials: !isMockMode, // en mock no hay cookies
    adapter: isMockMode ? mockAdapter : undefined,
    headers: {
        'Content-Type': 'application/json',
    },
});

if (isMockMode) {
    // eslint-disable-next-line no-console
    console.info('[apiClient] 🟢 MOCK MODE habilitado — sin backend real.');
}

/**
 * Un solo /auth/refresh a la vez. El backend rota el refresh en cada llamada: si varias peticiones
 * vencen juntas y cada una dispara su refresh, todas menos la primera llegan con un token ya rotado
 * (el backend las tolera 30 s, pero no hay por qué gastarlas). Comparten la misma promesa, que se
 * libera al terminar para que un 401 posterior vuelva a intentar.
 */
let refreshEnCurso: Promise<string> | null = null;

function refrescarAccess(): Promise<string> {
    if (!refreshEnCurso) {
        // Instancia aparte (axios pelado): si el refresh da 401 no vuelve a entrar al interceptor.
        refreshEnCurso = axios
            .post(`${baseURL}/auth/refresh`, {}, { withCredentials: true })
            .then((r) => r.data.data.accessToken as string)
            .finally(() => {
                refreshEnCurso = null;
            });
    }
    return refreshEnCurso;
}

/**
 * Las rutas del login contestan por sí mismas: un 401 (o un 400) de ellas NO es un access vencido. Si
 * el interceptor refrescaba con el 401 de verify-otp, sin sesión el refresh fallaba y el login mostraba
 * ese error en vez del mensaje del backend. /auth/refresh y /auth/logout además entrarían en rulo.
 */
const RUTAS_SIN_REFRESH = ['/auth/request-otp', '/auth/verify-otp', '/auth/refresh', '/auth/logout'];

function esRutaSinRefresh(url?: string): boolean {
    const camino = String(url || '').split('?')[0];
    return RUTAS_SIN_REFRESH.some((r) => camino.endsWith(r));
}

// Response interceptor for token refresh
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // En modo mock no hay refresh real: dejamos que el 401 burbujee
        // para que el AuthContext muestre el login.
        if (isMockMode) {
            return Promise.reject(error);
        }

        // If error is 401 and we haven't tried to refresh yet
        if (error.response?.status === 401 && !originalRequest._retry && !esRutaSinRefresh(originalRequest?.url)) {
            originalRequest._retry = true;

            try {
                const accessToken = await refrescarAccess();

                // Update auth header for future requests
                apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

                // Update auth header for CURRENT retry request
                originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;

                return apiClient(originalRequest);
            } catch (refreshError) {
                // Sin refresh no hay sesión: AuthContext la cierra al oír el aviso y ProtectedRoute lleva a
                // /login. La petición igual falla para quien la hizo.
                avisarSesionVencida();
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

/**
 * Set Access Token in headers
 */
export const setAuthToken = (token: string | null) => {
    if (token) {
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
        delete apiClient.defaults.headers.common['Authorization'];
    }
};
/**
 * Get Access Token from headers
 */
export const getAuthToken = () => {
    const authHeader = apiClient.defaults.headers.common['Authorization'] as string;
    if (authHeader && authHeader.startsWith('Bearer ')) {
        return authHeader.substring(7);
    }
    return null;
};

/**
 * El `role` de un JWT, o "" si no se puede leer. No valida nada —la firma la verifica el backend—: sólo
 * distingue una sesión de persona (el login por mail, role PERSONA) del acceso directo a la demo. Mismo
 * criterio que `marcaDelToken` en modules/campana-nueva/api.ts.
 */
export function rolDelToken(t: string | null | undefined): string {
    try {
        const payload = String(t || '').split('.')[1] || '';
        return String(JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))?.role || '');
    } catch {
        return '';
    }
}
